import { DrawContext, FabricImage, FabricObject, util as fabricUtils } from 'fabric';

import { ImageMask } from 'editor/src/store/design/types';

import { ObjectRect } from '../types';

import { getLocalMaskPoints } from './getMaskPolygon';

/**
 * GAPI-61891: soft (feathered) edges for image frames on the canvas.
 *
 * Same model as preflight: the mask shape (or the frame rectangle) is inset by feather / 2 and blurred with a Gaussian
 * of sigma = feather / 4, so the alpha is ~0 on the frame edge, ~0.5 at feather / 2 and ~1 at feather inside.
 * The blurred alpha is computed in JS (three box blurs approximate the Gaussian, no canvas filter support needed) on a
 * raster of at most FEATHER_MASK_MAX_SIDE px and used as an absolutely positioned image clip path, which fabric
 * applies with `destination-in`, i.e. as a soft alpha mask.
 */

export const FEATHER_MASK_MAX_SIDE = 512;
const SUPERSAMPLING = 2;

type Shape = ImageMask['shape'] | 'rect';

const isOutlineShape = (shape: Shape): shape is 'circle' | 'heart' | 'arch' =>
  shape === 'circle' || shape === 'heart' || shape === 'arch';

/** x ranges of a polygon on the horizontal line y (even-odd rule), sorted */
function getRowSpans(points: [number, number][], y: number): number[] {
  const xs: number[] = [];
  for (let i = 0, j = points.length - 1; i < points.length; j = i, i += 1) {
    const [xi, yi] = points[i];
    const [xj, yj] = points[j];
    if (yi > y !== yj > y) {
      xs.push(xi + ((y - yi) / (yj - yi)) * (xj - xi));
    }
  }
  return xs.sort((a, b) => a - b);
}

function isInSpans(spans: number[], x: number) {
  for (let i = 0; i + 1 < spans.length; i += 2) {
    if (x >= spans[i] && x <= spans[i + 1]) {
      return true;
    }
  }
  return false;
}

function isInside(shape: Shape, x: number, y: number, w: number, h: number, r: number) {
  if (x < 0 || y < 0 || x > w || y > h) {
    return false;
  }
  if (shape === 'ellipse') {
    const dx = (x - w / 2) / (w / 2);
    const dy = (y - h / 2) / (h / 2);
    return dx * dx + dy * dy <= 1;
  }
  if (shape === 'rounded-rect' && r > 0) {
    const cx = Math.min(Math.max(x, r), w - r);
    const cy = Math.min(Math.max(y, r), h - r);
    return (x - cx) ** 2 + (y - cy) ** 2 <= r * r;
  }
  return true;
}

/**
 * Coverage (0..1) of the shape inscribed in a w x h box inset by `inset` on every side, sampled on a width x height
 * pixel grid (SUPERSAMPLING^2 samples per pixel).
 */
export function rasterizeShape(
  width: number,
  height: number,
  shape: Shape,
  radius: number,
  inset: number,
): Float32Array {
  const out = new Float32Array(width * height);
  const w = width - 2 * inset;
  const h = height - 2 * inset;
  if (w <= 0 || h <= 0) {
    return out;
  }
  const r = Math.max(0, Math.min(radius - inset, w / 2, h / 2));
  const step = 1 / SUPERSAMPLING;
  const weight = 1 / (SUPERSAMPLING * SUPERSAMPLING);
  // outline shapes are tested against the same polygon the canvas clips to, one row of samples at a time
  const outline = isOutlineShape(shape) ? getLocalMaskPoints({ shape }, w, h, 0) : undefined;
  for (let py = 0; py < height; py += 1) {
    const rowSpans = outline
      ? Array.from({ length: SUPERSAMPLING }, (_, sy) => getRowSpans(outline, py + (sy + 0.5) * step - inset))
      : undefined;
    for (let px = 0; px < width; px += 1) {
      let covered = 0;
      for (let sy = 0; sy < SUPERSAMPLING; sy += 1) {
        for (let sx = 0; sx < SUPERSAMPLING; sx += 1) {
          const x = px + (sx + 0.5) * step - inset;
          const y = py + (sy + 0.5) * step - inset;
          if (rowSpans ? isInSpans(rowSpans[sy], x) : isInside(shape, x, y, w, h, r)) {
            covered += weight;
          }
        }
      }
      out[py * width + px] = covered;
    }
  }
  return out;
}

/** box sizes for n successive box blurs approximating a Gaussian of `sigma` (W. Jarosz / I. Kutskir) */
export function boxesForGauss(sigma: number, n = 3): number[] {
  const wIdeal = Math.sqrt((12 * sigma * sigma) / n + 1);
  let wl = Math.floor(wIdeal);
  if (wl % 2 === 0) {
    wl -= 1;
  }
  const wu = wl + 2;
  const mIdeal = (12 * sigma * sigma - n * wl * wl - 4 * n * wl - 3 * n) / (-4 * wl - 4);
  const m = Math.round(mIdeal);
  return Array.from({ length: n }, (_, i) => (i < m ? wl : wu));
}

function boxBlurLine(src: Float32Array, dst: Float32Array, start: number, stride: number, len: number, r: number) {
  // edges are treated as 0 (outside the frame nothing is visible)
  const norm = 1 / (2 * r + 1);
  let acc = 0;
  for (let i = 0; i <= r && i < len; i += 1) {
    acc += src[start + i * stride];
  }
  for (let i = 0; i < len; i += 1) {
    dst[start + i * stride] = acc * norm;
    const add = i + r + 1;
    const remove = i - r;
    if (add < len) {
      acc += src[start + add * stride];
    }
    if (remove >= 0) {
      acc -= src[start + remove * stride];
    }
  }
}

/** Separable Gaussian approximation (3 box blurs) of a width x height alpha raster. */
export function blurAlpha(alpha: Float32Array, width: number, height: number, sigma: number): Float32Array {
  if (sigma <= 0) {
    return alpha;
  }
  let a = alpha;
  let b = new Float32Array(alpha.length);
  boxesForGauss(sigma).forEach((size) => {
    const r = (size - 1) / 2;
    for (let y = 0; y < height; y += 1) {
      boxBlurLine(a, b, y * width, 1, width, r);
    }
    [a, b] = [b, a];
    for (let x = 0; x < width; x += 1) {
      boxBlurLine(a, b, x, width, height, r);
    }
    [a, b] = [b, a];
  });
  return a;
}

export interface FeatherAlpha {
  alpha: Float32Array;
  width: number;
  height: number;
}

/** Feathered alpha of a frame of widthPx x heightPx canvas px, at a resolution of at most FEATHER_MASK_MAX_SIDE. */
export function buildFeatherAlpha(
  widthPx: number,
  heightPx: number,
  mask: ImageMask | undefined,
  radiusPx: number,
  featherPx: number,
): FeatherAlpha {
  const scale = Math.min(1, FEATHER_MASK_MAX_SIDE / Math.max(widthPx, heightPx, 1));
  const width = Math.max(1, Math.round(widthPx * scale));
  const height = Math.max(1, Math.round(heightPx * scale));
  const feather = featherPx * scale;
  const inset = Math.max(0, Math.min(feather / 2, width / 2 - 1, height / 2 - 1));
  const coverage = rasterizeShape(width, height, mask?.shape ?? 'rect', radiusPx * scale, inset);
  return { alpha: blurAlpha(coverage, width, height, feather / 4), width, height };
}

/**
 * Image used as a soft clip path. Its own clip path (the hard content / product clip, absolutely positioned) is drawn
 * in the current clip layer context, like CustomFabricGroup's absoluteClipPath workaround: fabric's nested clip layer
 * loses the inverse transform of the clipped object, which misplaces nested absolute clip paths.
 */
export class FeatherMaskImage extends FabricImage {
  override _drawClipPath(ctx: CanvasRenderingContext2D, clipPath: FabricObject | undefined, context: DrawContext) {
    if (!clipPath) {
      return;
    }
    if (!clipPath._cacheCanvas) {
      clipPath._createCacheCanvas();
    }
    const cacheCanvas = clipPath._cacheCanvas;
    if (!cacheCanvas) {
      super._drawClipPath(ctx, clipPath, context);
      return;
    }
    clipPath._set('canvas', this.canvas);
    clipPath.shouldCache();
    clipPath._transformDone = true;
    (clipPath as any).renderCache({ forClipping: true });

    ctx.save();
    ctx.globalCompositeOperation = clipPath.inverted ? 'destination-out' : 'destination-in';
    if (clipPath.absolutePositioned) {
      const m = fabricUtils.invertTransform(this.calcTransformMatrix());
      ctx.transform(m[0], m[1], m[2], m[3], m[4], m[5]);
    }
    clipPath.transform(ctx);
    const { zoomX = 1, zoomY = 1, cacheTranslationX = 0, cacheTranslationY = 0 } = clipPath as any;
    ctx.scale(1 / zoomX, 1 / zoomY);
    ctx.drawImage(cacheCanvas, -cacheTranslationX, -cacheTranslationY);
    ctx.restore();
  }
}

interface FeatherCanvas {
  canvas: HTMLCanvasElement;
  width: number;
  height: number;
}

// LRU of finished alpha canvases: moving / rotating a frame or re-rendering reuses the canvas, only a new size,
// shape or feather builds a new one
const cache = new Map<string, FeatherCanvas>();
export const FEATHER_CACHE_SIZE = 64;

function getFeatherCanvas(
  widthPx: number,
  heightPx: number,
  mask: ImageMask | undefined,
  radiusPx: number,
  featherPx: number,
): FeatherCanvas | undefined {
  const key = [
    Math.round(widthPx),
    Math.round(heightPx),
    mask?.shape ?? 'rect',
    Math.round(radiusPx * 10),
    Math.round(featherPx * 10),
  ].join('|');
  const cached = cache.get(key);
  if (cached) {
    cache.delete(key);
    cache.set(key, cached);
    return cached;
  }

  const { alpha, width, height } = buildFeatherAlpha(widthPx, heightPx, mask, radiusPx, featherPx);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return undefined;
  }
  const imageData = ctx.createImageData(width, height);
  for (let i = 0; i < alpha.length; i += 1) {
    imageData.data[i * 4 + 3] = Math.round(alpha[i] * 255);
  }
  ctx.putImageData(imageData, 0, 0);

  const value = { canvas, width, height };
  if (cache.size >= FEATHER_CACHE_SIZE) {
    cache.delete(cache.keys().next().value as string);
  }
  cache.set(key, value);
  return value;
}

/**
 * Soft clip path for an image frame, or undefined when there is nothing to feather (or no 2d canvas, e.g. in tests).
 * `getHardClip` returns the regular content / product clip of the frame (absolutely positioned), nested inside so the
 * product clipping keeps its crisp vector edges. It is a callback so the polygon clip is only computed when the frame
 * is actually feathered.
 */
function getFeatherClipPath(
  frameRect: ObjectRect,
  mask: ImageMask | undefined,
  radiusPx: number,
  featherPx: number,
  getHardClip?: () => FabricObject | undefined,
): FabricObject | undefined {
  if (!(featherPx > 0) || frameRect.width <= 0 || frameRect.height <= 0) {
    return undefined;
  }
  const featherCanvas = getFeatherCanvas(frameRect.width, frameRect.height, mask, radiusPx, featherPx);
  if (!featherCanvas) {
    return undefined;
  }
  const { canvas, width, height } = featherCanvas;
  return new FeatherMaskImage(canvas, {
    left: frameRect.left,
    top: frameRect.top,
    angle: frameRect.angle,
    originX: 'left',
    originY: 'top',
    scaleX: frameRect.width / width,
    scaleY: frameRect.height / height,
    absolutePositioned: true,
    objectCaching: false,
    clipPath: getHardClip?.(),
  }) as unknown as FabricObject;
}

/**
 * Soft clip of an image element (undefined when it is not feathered, or has a perspective transform: the warp is not
 * previewed on the soft mask). `scale` derives the clip of a scaled copy of the frame (e.g. the drop shadow).
 */
export function getElementFeatherClip(
  element: { type: string; mask?: ImageMask; feather?: number },
  rect: ObjectRect,
  mm2px: (size: number) => number,
  scale = 1,
  getHardClip?: () => FabricObject | undefined,
  hasPerspective = false,
): FabricObject | undefined {
  if (element.type !== 'image' || !element.feather || element.feather <= 0 || hasPerspective) {
    return undefined;
  }
  const { mask, feather } = element;
  return getFeatherClipPath(rect, mask, mm2px(mask?.radius ?? 0) * scale, mm2px(feather) * scale, getHardClip);
}

export default getFeatherClipPath;
